import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch, ApiError } from '../api/client'
import type {
  AddUserRequest,
  AllowedPattern,
  GlowUser,
  GlowUsersResponse,
  UpdateUserRequest,
} from '../api/types'

// useGlowUsers drives the granular user-management surface that replaced
// the old full-replace allowlist (CLAUDE.md §3). Each mutation hits its own
// endpoint and invalidates ['glow-users', id] so the table re-reads the
// authoritative server state — no local dirty/draft machinery.

const usersKey = (glowID: string) => ['glow-users', glowID] as const

// Hard ceiling on auto-pagination so a pathological glow (or a server bug that
// never empties `next`) can't spin forever. 100 pages × the server's
// 500-default page size = 50k users, matching the per-glow pattern-user cap
// (MAX_PATTERN_USERS_PER_GLOW default 50000, CP3).
const maxUserPages = 100

export function useGlowUsers(glowID: string) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: usersKey(glowID),
    // GET /v1/glows/{id}/users is cursor-paginated ({users, next}, CP3); follow
    // `next` and concatenate every page so the table shows the COMPLETE roster,
    // never a silently-truncated first page. Most glows fit in one page, so this
    // is a single request in the common case.
    queryFn: async ({ signal }) => {
      const all: GlowUser[] = []
      let cursor = ''
      for (let page = 0; page < maxUserPages; page++) {
        const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''
        const res = await apiFetch<GlowUsersResponse>(
          `/v1/glows/${glowID}/users${suffix}`,
          idTokenProvider,
          { signal },
        )
        all.push(...(res.users ?? []))
        if (!res.next) {
          break
        }
        cursor = res.next
      }
      return all
    },
    enabled: !!user && !!glowID,
  })
}

export function useAddUser(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AddUserRequest) =>
      apiFetch<GlowUser>(`/v1/glows/${glowID}/users`, idTokenProvider, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKey(glowID) })
    },
  })
}

export function useUpdateUser(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: UpdateUserRequest }) =>
      apiFetch<GlowUser>(
        `/v1/glows/${glowID}/users/${encodeURIComponent(uid)}`,
        idTokenProvider,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKey(glowID) })
    },
  })
}

export function useDeleteUser(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uid: string) =>
      apiFetch<void>(
        `/v1/glows/${glowID}/users/${encodeURIComponent(uid)}`,
        idTokenProvider,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: usersKey(glowID) })
    },
  })
}

// useUpdatePatterns full-replaces the glow's domain-pattern list
// (PUT /v1/glows/{id}/patterns). Patterns are still a single array on the
// glow, so this stays a whole-list write — distinct from the per-user
// endpoints above. Invalidates the glow query because the new patterns ride
// on the Glow doc.
export function useUpdatePatterns(glowID: string) {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patterns: AllowedPattern[]) =>
      apiFetch<void>(`/v1/glows/${glowID}/patterns`, idTokenProvider, {
        method: 'PUT',
        body: { allowed_patterns: patterns },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glow', glowID] })
    },
  })
}

// glowUserErrorMessage maps the granular endpoints' error codes
// (402 subscription gates, 409 conflicts) to messages the UI can show
// inline. Falls back to the server message for anything else.
export function glowUserErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'subscription_required':
        return 'Adding users beyond the owner requires a subscription.'
      case 'user_exists':
        return 'That user is already on the list.'
      case 'invalid_mobile':
        return 'Enter a valid mobile number in international format, e.g. +1 555 123 4567.'
      case 'mobile_exists':
        return 'A user with that mobile is already on the list.'
      case 'owner_immutable':
        return 'The owner cannot be removed.'
      default:
        return e.message
    }
  }
  return fallback
}
